/**
 * LocationStep Component
 * Step 2: Service address entry with improved UI consistency
 */

import { useFormContext } from 'react-hook-form';
import type { OrderForm } from '../../../schemas/order.schema';
import { MapPinIcon } from 'lucide-react';
import { PricingDisplay } from '../../../components/order/PricingDisplay';
import { DeliveryTimeIndicator } from '../../../components/delivery/DeliveryTimeIndicator';
import { useProviderConfig } from '../../../contexts/ProviderConfigContext';
import { calculateDeliveryEstimate } from '../../../lib/utils/delivery-time';
import { FieldError } from '../../../components/widget/FieldError';
import { useZipValidation } from '../../../hooks/useZipValidation';

interface LocationStepProps {
  pricingInfo?: {
    type: 'fixed' | 'dynamic';
    fixedRates?: {
      '10Y': number;
      '20Y': number;
      '30Y': number;
      '40Y': number;
    };
    dynamicQuote?: {
      estimatedPriceCents: number;
      breakdown: {
        baseCostCents: number;
        distanceCostCents: number;
        timeCostCents: number;
        markupPercent: number;
      };
      estimatedDriveMinutes: number;
      estimatedDistanceMiles: number;
    };
  } | null;
  isValidating?: boolean;
  pricingUnavailable?: boolean;
}

export function LocationStep({ pricingInfo, isValidating, pricingUnavailable }: LocationStepProps) {
  const { register, formState: { errors }, setValue, watch } = useFormContext<OrderForm>();
  const { config } = useProviderConfig();
  const { format: formatZip, inputProps: zipInputProps } = useZipValidation();

  // Watch ZIP code for formatting
  const zipCode = watch('zipCode');

  // Calculate delivery estimate based on provider settings
  const deliveryEstimate = calculateDeliveryEstimate(config?.deliveryTimeSettings);

  // Handle ZIP code formatting on change
  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatZip(e.target.value);
    setValue('zipCode', formatted);
  };

  return (
    <div className="space-y-6">
      {/* Header Section - Matching ServiceCatalog typography */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-50 rounded-lg">
            <MapPinIcon className="h-6 w-6 text-primary-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900">
            Service Location
          </h2>
        </div>
        <p className="text-lg text-gray-600 leading-relaxed">
          Enter the address where you would like the container delivered.
        </p>
      </div>

      {/* Form Fields Container */}
      <div className="space-y-5">
        {/* Street Address */}
        <div>
          <label htmlFor="street" className="block text-sm font-semibold text-gray-700 mb-2">
            Street Address <span className="text-red-500">*</span>
          </label>
          <input
            {...register('street')}
            type="text"
            id="street"
            placeholder="123 Main Street"
            autoComplete="address-line1"
            className="w-full min-h-[48px] px-4 py-3 text-base border border-gray-300 rounded-lg shadow-sm transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 hover:border-gray-400"
            aria-required="true"
            aria-invalid={errors.street ? "true" : "false"}
            aria-describedby={errors.street ? "street-error" : undefined}
          />
          <FieldError id="street-error" message={errors.street?.message} />
        </div>

        {/* City, State, ZIP Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-6 gap-4">
          {/* City */}
          <div className="sm:col-span-3">
            <label htmlFor="city" className="block text-sm font-semibold text-gray-700 mb-2">
              City <span className="text-red-500">*</span>
            </label>
            <input
              {...register('city')}
              type="text"
              id="city"
              placeholder="San Luis Obispo"
              autoComplete="address-level2"
              className="w-full min-h-[48px] px-4 py-3 text-base border border-gray-300 rounded-lg shadow-sm transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 hover:border-gray-400"
              aria-required="true"
              aria-invalid={errors.city ? "true" : "false"}
              aria-describedby={errors.city ? "city-error" : undefined}
            />
            <FieldError id="city-error" message={errors.city?.message} />
          </div>

          {/* State */}
          <div className="sm:col-span-1">
            <label htmlFor="state" className="block text-sm font-semibold text-gray-700 mb-2">
              State <span className="text-red-500">*</span>
            </label>
            <select
              {...register('state')}
              id="state"
              autoComplete="address-level1"
              className="w-full min-h-[48px] px-4 py-3 text-[16px] border border-gray-300 rounded-lg shadow-sm transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 hover:border-gray-400 appearance-none bg-white"
              aria-required="true"
              aria-invalid={errors.state ? "true" : "false"}
              aria-describedby={errors.state ? "state-error" : undefined}
            >
              <option value="CA">CA</option>
              <option value="OR">OR</option>
              <option value="WA">WA</option>
              <option value="NV">NV</option>
              <option value="AZ">AZ</option>
            </select>
            <FieldError id="state-error" message={errors.state?.message} />
          </div>

          {/* ZIP Code */}
          <div className="sm:col-span-2">
            <label htmlFor="zipCode" className="block text-sm font-semibold text-gray-700 mb-2">
              ZIP Code <span className="text-red-500">*</span>
            </label>
            <input
              {...register('zipCode', {
                onChange: handleZipChange
              })}
              {...zipInputProps}
              inputMode="numeric"
              pattern="[0-9]*"
              id="zipCode"
              placeholder="93401"
              maxLength={10}
              value={zipCode || ''}
              className="w-full min-h-[48px] px-4 py-3 text-base border border-gray-300 rounded-lg shadow-sm transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 hover:border-gray-400"
              aria-required="true"
              aria-invalid={errors.zipCode ? "true" : "false"}
              aria-describedby={errors.zipCode ? "zipCode-error" : undefined}
            />
            <FieldError id="zipCode-error" message={errors.zipCode?.message} />
          </div>
        </div>

        {/* Placement Instructions - Optional */}
        <div>
          <label htmlFor="placementInstructions" className="block text-sm font-semibold text-gray-700 mb-2">
            Placement Instructions <span className="text-gray-500 font-normal">(Optional)</span>
          </label>
          <textarea
            {...register('placementInstructions')}
            id="placementInstructions"
            rows={3}
            placeholder="e.g., Place in the driveway, left side near the garage"
            className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg shadow-sm transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 hover:border-gray-400 resize-none"
          />
          <p className="mt-1 text-sm text-gray-500">
            Help our driver know exactly where to place your container.
          </p>
        </div>
      </div>

      {/* Loading Indicator - shown during validation */}
      {isValidating && (
        <div
          className="flex items-center justify-center p-4 bg-blue-50 rounded-md border border-blue-200"
          role="status"
          aria-live="polite"
          aria-label="Validating service area"
        >
          <svg
            className="animate-spin h-5 w-5 mr-2 text-blue-600"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span className="text-sm text-blue-700 font-medium">
            Validating service area and calculating pricing...
          </span>
        </div>
      )}

      {/* Pricing Unavailable Notice */}
      {pricingUnavailable && (
        <div
          className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-md"
          role="alert"
          aria-live="polite"
        >
          <div className="flex items-start gap-3">
            <svg
              className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-semibold text-yellow-900 mb-1">
                Pricing Unavailable
              </p>
              <p className="text-sm text-yellow-700">
                Service available but pricing unavailable at this time. Please contact us for a quote.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Pricing Display - shown after successful validation */}
      {pricingInfo && (
        <div className="animate-in fade-in-0 slide-in-from-top-4 duration-300">
          <PricingDisplay
            type={pricingInfo.type}
            fixedRates={pricingInfo.fixedRates}
            dynamicQuote={pricingInfo.dynamicQuote}
          />
        </div>
      )}

      {/* Delivery Time Indicator - shown after pricing */}
      {pricingInfo && config?.deliveryTimeSettings?.enabled && (
        <div className="animate-in fade-in-0 slide-in-from-top-4 duration-300">
          <DeliveryTimeIndicator
            estimatedDelivery={deliveryEstimate}
            variant="inline"
            showRushOption={false}
          />
        </div>
      )}

      {/* Service Area Information */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <svg
            className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div className="flex-1">
            <p className="text-sm font-semibold text-blue-900 mb-1">
              Service Area
            </p>
            <p className="text-sm text-blue-800">
              We currently serve San Luis Obispo County and surrounding areas.
              If you&apos;re outside our service area, please call us to discuss options.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}